import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAlertsLive } from '@/features/alerts/hooks/useAlerts';
import { useRecommendationsLive } from '@/features/recommendations/hooks/useRecommendations';
import { recommendationsApi } from '@/features/recommendations/api/recommendations.api';
import { useDeploymentDetail } from '@/features/deployments/hooks/useDeployments';
import { RecommendationDrawer } from '@/features/recommendations/ui/RecommendationDrawer';
import { LIVE_VARIANTS, DEFAULT_VARIANT } from '@/features/liveMode/variants';
import { furnaceSvgUrl } from '@/features/liveMode/liveModeMeta';
import { AlertDetailDrawer } from './AlertDetailDrawer';

/**
 * Operator "Live Mode" — the always-on control-room screen.
 *
 * Composition root: feature folders can't import each other (ESLint
 * `boundaries`), so the page owns the cross-feature live queries AND the detail
 * drawers. The liveMode feature just emits onSelectRec/onSelectAlert; clicking a
 * card opens the drawer in-place over the live screen. Recommendations reuse the
 * real RecommendationDrawer; alerts use a page-local twin (the real alert modal
 * is inline in the alerts page and can't be imported here).
 */
export default function LiveModePage({ variant = DEFAULT_VARIANT }) {
  const { id } = useParams();
  const alertsQ = useAlertsLive(id);
  const recsQ = useRecommendationsLive(id);
  const depQ = useDeploymentDetail(id);
  const deployment = depQ.data;

  const [selectedRec, setSelectedRec] = useState(null);
  const [recOpen, setRecOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [alertOpen, setAlertOpen] = useState(false);

  const Screen = (LIVE_VARIANTS[variant] ?? LIVE_VARIANTS[DEFAULT_VARIANT]).Screen;

  // Inline rec action (the "act on all" work-queue) — feature folders can't reach
  // the recommendations API, so the page owns it. Submits the status change then
  // refetches the live recs; useLiveSimulator re-seeds on the new array, so the
  // acted rec leaves the actionable queue and the headline count updates.
  async function handleQuickAct(rec, action) {
    await recommendationsApi.submitAction(id, rec.id, { action });
    await recsQ.refetch();
  }

  return (
    <>
      <Screen
        deploymentName={deployment?.name}
        svgUrl={furnaceSvgUrl(deployment?.plantType)}
        seedAlerts={alertsQ.data}
        seedRecs={recsQ.data}
        isLoading={alertsQ.isLoading || recsQ.isLoading}
        isError={alertsQ.isError && recsQ.isError}
        onSelectRec={(rec) => { setAlertOpen(false); setSelectedRec(rec); setRecOpen(true); }}
        onSelectAlert={(alert) => { setRecOpen(false); setSelectedAlert(alert); setAlertOpen(true); }}
        onQuickAct={handleQuickAct}
      />

      {selectedRec && (
        <RecommendationDrawer
          rec={selectedRec}
          depId={id}
          isHistorical={false}
          isOpen={recOpen}
          onClose={() => setRecOpen(false)}
        />
      )}

      {selectedAlert && (
        <AlertDetailDrawer
          alert={selectedAlert}
          isOpen={alertOpen}
          onClose={() => setAlertOpen(false)}
          onExited={() => setSelectedAlert(null)}
        />
      )}
    </>
  );
}
